/**
 * Assignment Card Component
 * Displays assignment information in a card layout
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, DollarSign, Clock, Eye, MessageSquare, FileText, CheckCircle, AlertTriangle } from 'lucide-react';

const AssignmentCard = ({ assignment, isOwner = false }) => {
  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const d = new Date(date);
    return d.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Get status badge color and text
  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return {
          color: 'bg-green-100 text-green-800',
          text: 'Open'
        };
      case 'assigned':
        return {
          color: 'bg-blue-100 text-blue-800',
          text: 'Assigned'
        };
      case 'completed':
        return {
          color: 'bg-purple-100 text-purple-800',
          text: 'Completed'
        };
      case 'cancelled':
        return {
          color: 'bg-red-100 text-red-800',
          text: 'Cancelled'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          text: 'Unknown'
        };
    }
  };
  
  // Check if assignment is due soon (within 2 days)
  const isDueSoon = () => {
    if (!assignment.dueDate) return false;
    
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 0 && diffDays <= 2;
  };
  
  // Get days remaining text
  const getDaysRemaining = () => {
    if (!assignment.dueDate) return 'No due date';
    
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Past due';
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else {
      return `${diffDays} days left`;
    }
  };
  
  const statusBadge = getStatusBadge(assignment.status);
  const dueSoon = isDueSoon() && assignment.status === 'open';
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition">
      {/* Card Header with Status */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <span className={`text-xs px-2 py-1 rounded-full ${statusBadge.color}`}>
          {statusBadge.text}
        </span>
        <div className="flex items-center space-x-3 text-xs text-gray-500">
          <span className="flex items-center">
            <Eye size={14} className="mr-1" />
            {assignment.viewCount || 0}
          </span>
          <span className="flex items-center">
            <MessageSquare size={14} className="mr-1" />
            {assignment.offerCount || 0}
          </span>
        </div>
      </div>
      
      {/* Card Body */}
      <Link to={`/assignments/${assignment.id}`} className="block p-4">
        <h3 className="text-lg font-medium text-gray-800 mb-2 line-clamp-1">
          {assignment.title}
        </h3>
        
        <div className="flex flex-wrap text-sm text-gray-600 mb-3">
          <span className="mr-3 mb-1">{assignment.subject}</span>
          {assignment.course && (
            <>
              <span className="mr-3 mb-1">•</span>
              <span className="mr-3 mb-1">{assignment.course}</span>
            </>
          )}
          <span className="mr-3 mb-1">•</span>
          <span className="mb-1">{assignment.university}</span>
        </div>
        
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {assignment.description}
        </p>
        
        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
          {/* Due Date */}
          <div className="flex items-center text-gray-600">
            <Calendar size={16} className="mr-2 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Due Date</p>
              <p className={`font-medium ${dueSoon ? 'text-red-600' : ''}`}>
                {formatDate(assignment.dueDate)}
              </p>
            </div>
          </div>
          
          {/* Budget */}
          <div className="flex items-center text-gray-600">
            <DollarSign size={16} className="mr-2 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Budget</p>
              <p className="font-medium">R{assignment.budget}</p>
            </div>
          </div>
          
          {/* Time Estimate */}
          <div className="flex items-center text-gray-600">
            <Clock size={16} className="mr-2 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Time Estimate</p>
              <p className="font-medium">{assignment.timeEstimate} hours</p>
            </div>
          </div>
          
          {/* File Type */}
          <div className="flex items-center text-gray-600">
            <FileText size={16} className="mr-2 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Attachment</p>
              <p className="font-medium">
                {assignment.fileName ? (
                  assignment.fileName.split('.').pop().toUpperCase()
                ) : (
                  'File'
                )}
              </p>
            </div>
          </div>
        </div>
      </Link>
      
      {/* Card Footer */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
        {/* Days Remaining */}
        {dueSoon ? (
          <span className="text-xs flex items-center text-red-600 font-medium">
            <AlertTriangle size={14} className="mr-1" />
            {getDaysRemaining()}
          </span>
        ) : (
          <span className="text-xs text-gray-500">
            {getDaysRemaining()}
          </span>
        )}
        
        {/* Status or Action */}
        {isOwner ? (
          <div>
            {assignment.status === 'assigned' && (
              <Link
                to={`/assignments/${assignment.id}`}
                className="text-xs px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition inline-flex items-center"
              >
                <CheckCircle size={12} className="mr-1" />
                Mark Complete
              </Link>
            )}
            {assignment.status === 'open' && (
              <Link
                to={`/assignments/${assignment.id}`}
                className="text-xs px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition inline-flex items-center"
              >
                <MessageSquare size={12} className="mr-1" />
                View Offers ({assignment.offerCount || 0})
              </Link>
            )}
            {assignment.status === 'completed' && (
              <span className="text-xs flex items-center text-purple-600 font-medium">
                <CheckCircle size={14} className="mr-1" />
                Completed
              </span>
            )}
          </div>
        ) : (
          <Link
            to={`/assignments/${assignment.id}`}
            className={`text-xs px-3 py-1.5 rounded-md transition inline-flex items-center ${
              assignment.status === 'open'
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-200 text-gray-700 cursor-not-allowed'
            }`}
            onClick={(e) => {
              if (assignment.status !== 'open') {
                e.preventDefault();
              }
            }}
          >
            {assignment.status === 'open' ? (
              <>
                <MessageSquare size={12} className="mr-1" />
                Make Offer
              </>
            ) : (
              <>
                <AlertTriangle size={12} className="mr-1" />
                Not Available
              </>
            )}
          </Link>
        )}
      </div>
    </div>
  );
};

export default AssignmentCard;